import { c as _c } from "react/compiler-runtime";
import { useTranslation } from 'react-i18next';
import { useSuggestionsForNode, useSuggestionsForRulesMeta } from '../../store/suggestions';
import type { RulesNode, Suggestion } from '../../store/types';
import InlineEditPair from './primitives/inline-edit-pair';
import PatchCard from './primitives/patch-card';
import { diffField } from './utils/diff-field';
type Props = {
  rulesNode: RulesNode;
};

// Name/key/default refinements. Rendered at the top of the node, next to the fields they
// edit, so they lead the canvas order — kept in sync with orderSuggestionsByCanvas.
export const RulesMetaSuggestions = t0 => {
  const $ = _c(7);
  const {
    rulesNode
  } = t0;
  const metaSuggestions = useSuggestionsForRulesMeta(rulesNode.id);
  let t1;
  if ($[0] !== metaSuggestions || $[1] !== rulesNode) {
    let t2;
    if ($[3] !== rulesNode) {
      t2 = s => <RulesMetaPatchCard key={s.id} suggestion={s} rulesNode={rulesNode} />;
      $[3] = rulesNode;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = metaSuggestions.map(t2);
    $[0] = metaSuggestions;
    $[1] = rulesNode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

// Whole-node ops (remove / replace). Rendered at the bottom of the node.
export const RulesNodeSuggestions = t0 => {
  const $ = _c(7);
  const {
    rulesNode
  } = t0;
  const nodeSuggestions = useSuggestionsForNode(rulesNode.id);
  let t1;
  if ($[0] !== nodeSuggestions || $[1] !== rulesNode) {
    let t2;
    if ($[3] !== rulesNode) {
      t2 = s => <RulesNodePatchCard key={s.id} suggestion={s} rulesNode={rulesNode} />;
      $[3] = rulesNode;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = nodeSuggestions.map(t2);
    $[0] = nodeSuggestions;
    $[1] = rulesNode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <>{t1}</>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type PatchProps = {
  suggestion: Suggestion;
  rulesNode: RulesNode;
};
const RulesMetaPatchCard = t0 => {
  const $ = _c(31);
  const {
    suggestion,
    rulesNode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.rules"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.canvas.suggestion.labels"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    t: tLabels
  } = useTranslation("agents", t2);
  const op = suggestion.operation;
  if (op.kind !== "update_rules_node_meta") {
    return null;
  }
  const isStale = !suggestion.canApply;
  let t3;
  if ($[2] !== op.name || $[3] !== rulesNode.name) {
    t3 = diffField(rulesNode.name, op.name);
    $[2] = op.name;
    $[3] = rulesNode.name;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const nameChange = t3;
  let t4;
  if ($[5] !== op.key || $[6] !== rulesNode.key) {
    t4 = diffField(rulesNode.key, op.key);
    $[5] = op.key;
    $[6] = rulesNode.key;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const keyChange = t4;
  let t5;
  if ($[8] !== op.defaultValue || $[9] !== rulesNode.defaultValue) {
    t5 = diffField(rulesNode.defaultValue, op.defaultValue);
    $[8] = op.defaultValue;
    $[9] = rulesNode.defaultValue;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const defaultValueChange = t5;
  if (!nameChange && !keyChange && !defaultValueChange) {
    return null;
  }
  const t6 = suggestion.id;
  let t7;
  if ($[11] !== t) {
    t7 = t(_temp);
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== nameChange || $[14] !== tLabels) {
    t8 = nameChange && <InlineEditPair label={tLabels(_temp2)} current={nameChange.from} suggested={nameChange.to} />;
    $[13] = nameChange;
    $[14] = tLabels;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== keyChange || $[17] !== tLabels) {
    t9 = keyChange && <InlineEditPair label={tLabels(_temp3)} current={keyChange.from} suggested={keyChange.to} />;
    $[16] = keyChange;
    $[17] = tLabels;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== defaultValueChange || $[20] !== tLabels) {
    t10 = defaultValueChange && <InlineEditPair label={tLabels(_temp4)} current={defaultValueChange.from} suggested={defaultValueChange.to} />;
    $[19] = defaultValueChange;
    $[20] = tLabels;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8 || $[24] !== t9) {
    t11 = <div className="flex flex-col gap-2">{t8}{t9}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== isStale || $[27] !== suggestion.id || $[28] !== t11 || $[29] !== t7) {
    t12 = <PatchCard suggestionId={t6} note={t7} isStale={isStale} showDiffToggle={true}>{t11}</PatchCard>;
    $[26] = isStale;
    $[27] = suggestion.id;
    $[28] = t11;
    $[29] = t7;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
};
const RulesNodePatchCard = t0 => {
  const $ = _c(24);
  const {
    suggestion,
    rulesNode
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.suggestion.rules"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const op = suggestion.operation;
  const isStale = !suggestion.canApply;
  if (op.kind === "remove_node") {
    const t2 = suggestion.id;
    let t3;
    if ($[1] !== t) {
      t3 = t(_temp5);
      $[1] = t;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== rulesNode.name || $[4] !== t) {
      t4 = t(_temp6, {
        name: rulesNode.name
      });
      $[3] = rulesNode.name;
      $[4] = t;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t4) {
      t5 = <p className="text-caption-3 text-tertiary">{t4}</p>;
      $[6] = t4;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== isStale || $[9] !== suggestion.id || $[10] !== t3 || $[11] !== t5) {
      t6 = <PatchCard suggestionId={t2} note={t3} isStale={isStale}>{t5}</PatchCard>;
      $[8] = isStale;
      $[9] = suggestion.id;
      $[10] = t3;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (op.kind === "update_node" && op.node.kind === "rules") {
    const t2 = suggestion.id;
    let t3;
    if ($[13] !== t) {
      t3 = t(_temp7);
      $[13] = t;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[15] !== t) {
      t4 = t(_temp8);
      $[15] = t;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    let t5;
    if ($[17] !== t4) {
      t5 = <p className="text-caption-3 text-tertiary">{t4}</p>;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== isStale || $[20] !== suggestion.id || $[21] !== t3 || $[22] !== t5) {
      t6 = <PatchCard suggestionId={t2} note={t3} isStale={isStale}>{t5}</PatchCard>;
      $[19] = isStale;
      $[20] = suggestion.id;
      $[21] = t3;
      $[22] = t5;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    return t6;
  }
  return null;
};
function _temp($) {
  return $.refinedMeta;
}
function _temp2($_0) {
  return $_0.name;
}
function _temp3($_1) {
  return $_1.outputKey;
}
function _temp4($_2) {
  return $_2.defaultValue;
}
function _temp5($) {
  return $.remove;
}
function _temp6($_0) {
  return $_0.removeBody;
}
function _temp7($_1) {
  return $_1.replace;
}
function _temp8($_2) {
  return $_2.replaceBody;
}